import React from 'react';

function FairValueTable({ data, onAddToWatchlist, watchedStocks }) {
  const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return '--';
    return `$${Number(value).toFixed(2)}`;
  };

  const formatPercent = (value) => {
    if (value == null || isNaN(value)) return '--';
    const num = Number(value) * 100;
    const color = num >= 0 ? 'text-green-400' : 'text-red-400';
    return <span className={color}>{num > 0 ? '+' : ''}{num.toFixed(1)}%</span>;
  };

  const formatNumber = (value) => {
    if (value == null || isNaN(value)) return '--';
    return Number(value).toFixed(2);
  };

  const isWatched = (ticker) => {
    return watchedStocks?.some(stock => stock.ticker === ticker);
  };

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">No fair value data available. Run analysis with "Fair Value" selected.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-900/40 rounded"></div>
          <span className="text-gray-400">Avg P/E Scenarios</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-900/40 rounded"></div>
          <span className="text-gray-400">Low P/E Scenarios (Bear Case)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-purple-900/40 rounded"></div>
          <span className="text-gray-400">High P/E Scenarios (Bull Case)</span>
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-700 rounded-lg">
        <table className="w-full text-sm min-w-max">
          <thead>
            <tr className="border-b border-gray-700" style={{ backgroundColor: '#1f2937' }}>
              <th className="text-left py-3 px-3 sticky left-0 z-10 font-semibold" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Ticker</th>
              <th className="text-right py-3 px-3 font-semibold" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Current Price</th>
              <th className="text-right py-3 px-3 font-semibold" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Current P/E</th>
              <th className="text-right py-3 px-3 font-semibold" style={{ backgroundColor: '#1f2937', color: '#9ca3af' }}>Avg 5Y P/E</th>
              <th className="text-right py-3 px-3 font-semibold" style={{ backgroundColor: '#1f2937', color: '#9ca3af' }}>Low 5Y P/E</th>
              <th className="text-right py-3 px-3 font-semibold" style={{ backgroundColor: '#1f2937', color: '#9ca3af' }}>High 5Y P/E</th>
              <th className="text-right py-3 px-3 font-semibold" style={{ backgroundColor: '#1f2937', color: '#9ca3af' }}>EPS Est Curr Yr</th>
              <th className="text-right py-3 px-3 font-semibold" style={{ backgroundColor: '#1f2937', color: '#9ca3af' }}>EPS Est Next Yr</th>
              {/* Fair Values - Current Year */}
              <th className="text-right py-3 px-3 font-semibold bg-blue-900/30" style={{ color: '#60a5fa' }}>Avg FV Curr Yr</th>
              <th className="text-right py-3 px-3 font-semibold bg-blue-900/30" style={{ color: '#60a5fa' }}>%Chg</th>
              <th className="text-right py-3 px-3 font-semibold bg-green-900/30" style={{ color: '#4ade80' }}>Low FV Curr Yr</th>
              <th className="text-right py-3 px-3 font-semibold bg-green-900/30" style={{ color: '#4ade80' }}>%Chg</th>
              <th className="text-right py-3 px-3 font-semibold bg-purple-900/30" style={{ color: '#c084fc' }}>High FV Curr Yr</th>
              <th className="text-right py-3 px-3 font-semibold bg-purple-900/30" style={{ color: '#c084fc' }}>%Chg</th>
              {/* Fair Values - Next Year */}
              <th className="text-right py-3 px-3 font-semibold bg-blue-900/50" style={{ color: '#60a5fa' }}>Avg FV Next Yr</th>
              <th className="text-right py-3 px-3 font-semibold bg-blue-900/50" style={{ color: '#60a5fa' }}>%Chg</th>
              <th className="text-right py-3 px-3 font-semibold bg-green-900/50" style={{ color: '#4ade80' }}>Low FV Next Yr</th>
              <th className="text-right py-3 px-3 font-semibold bg-green-900/50" style={{ color: '#4ade80' }}>%Chg</th>
              <th className="text-right py-3 px-3 font-semibold bg-purple-900/50" style={{ color: '#c084fc' }}>High FV Next Yr</th>
              <th className="text-right py-3 px-3 font-semibold bg-purple-900/50" style={{ color: '#c084fc' }}>%Chg</th>
              {/* Actions */}
              <th className="text-center py-3 px-3 sticky right-0 z-10" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index} className={`border-b border-gray-800 hover:bg-gray-700/30 ${index % 2 === 0 ? 'bg-gray-800/30' : ''}`}>
                <td className="py-2 px-3 font-medium text-white sticky left-0" style={{ backgroundColor: index % 2 === 0 ? '#1f2937' : '#111827' }}>
                  {isWatched(row.Ticker) && <span className="mr-1">⭐</span>}
                  {row.Ticker}
                </td>
                <td className="py-2 px-3 text-right text-white font-medium">{formatCurrency(row['Current Price'])}</td>
                <td className="py-2 px-3 text-right text-white">{formatNumber(row['Current P/E'])}</td>
                <td className="py-2 px-3 text-right text-gray-300">{formatNumber(row['Avg 5Y P/E'])}</td>
                <td className="py-2 px-3 text-right text-gray-300">{formatNumber(row['Low 5Y P/E'])}</td>
                <td className="py-2 px-3 text-right text-gray-300">{formatNumber(row['High 5Y P/E'])}</td>
                <td className="py-2 px-3 text-right text-gray-300">{formatCurrency(row['EPS Est Current Yr'])}</td>
                <td className="py-2 px-3 text-right text-gray-300">{formatCurrency(row['EPS Est Next Yr'])}</td>
                {/* Fair Values - Current Year */}
                <td className="py-2 px-3 text-right text-white bg-blue-900/20">{formatCurrency(row['Avg FV Current Yr'])}</td>
                <td className="py-2 px-3 text-right bg-blue-900/20">{formatPercent(row['Avg FV Curr Yr %Chg'])}</td>
                <td className="py-2 px-3 text-right text-white bg-green-900/20">{formatCurrency(row['Low FV Current Yr'])}</td>
                <td className="py-2 px-3 text-right bg-green-900/20">{formatPercent(row['Low FV Curr Yr %Chg'])}</td>
                <td className="py-2 px-3 text-right text-white bg-purple-900/20">{formatCurrency(row['High FV Current Yr'])}</td>
                <td className="py-2 px-3 text-right bg-purple-900/20">{formatPercent(row['High FV Curr Yr %Chg'])}</td>
                {/* Fair Values - Next Year */}
                <td className="py-2 px-3 text-right text-white bg-blue-900/30">{formatCurrency(row['Avg FV Next Yr'])}</td>
                <td className="py-2 px-3 text-right bg-blue-900/30">{formatPercent(row['Avg FV Next Yr %Chg'])}</td>
                <td className="py-2 px-3 text-right text-white bg-green-900/30">{formatCurrency(row['Low FV Next Yr'])}</td>
                <td className="py-2 px-3 text-right bg-green-900/30">{formatPercent(row['Low FV Next Yr %Chg'])}</td>
                <td className="py-2 px-3 text-right text-white bg-purple-900/30">{formatCurrency(row['High FV Next Yr'])}</td>
                <td className="py-2 px-3 text-right bg-purple-900/30">{formatPercent(row['High FV Next Yr %Chg'])}</td>
                {/* Actions */}
                <td className="py-2 px-3 text-center sticky right-0" style={{ backgroundColor: index % 2 === 0 ? '#1f2937' : '#111827' }}>
                  {!isWatched(row.Ticker) && onAddToWatchlist && (
                    <button
                      onClick={() => onAddToWatchlist(row.Ticker)}
                      className="text-blue-400 hover:text-blue-300 text-xs px-2 py-1 rounded border border-blue-400 hover:border-blue-300"
                    >
                      Watch
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center text-sm text-gray-400">
        <span>
          {data.length} ticker{data.length !== 1 ? 's' : ''} analyzed
        </span>
        <span>
          Fair Value = P/E Ratio × EPS Forecast | %Chg = Upside/Downside from Current Price
        </span>
      </div>
    </div>
  );
}

export default FairValueTable;
